import {
  dateSchema,
  parseToDateValue,
  parseYes,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { openFundFields } from "./open_fund_fields_config";

// Utility functions for form data preprocessing

// Normalize date string to ISO 8601 format (YYYY-MM-DD)
const normalizeDateString = (dateStr: string): string => {
  if (!dateStr || typeof dateStr !== "string") return dateStr;

  // Check if it's already in correct format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

  // Handle format with single digits (YYYY-M-D or YYYY-MM-D or YYYY-M-DD)
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    const [year, month, day] = parts;
    // Pad month and day with zeros if needed
    const paddedMonth = month.padStart(2, "0");
    const paddedDay = day.padStart(2, "0");
    return `${year}-${paddedMonth}-${paddedDay}`;
  }

  return dateStr; // Return original if parsing fails
};

// Enhanced parseToDateValue that handles non-ISO dates
const parseToDateValueEnhanced = (value: unknown) => {
  if (typeof value === "string") {
    // Normalize the date string first
    const normalizedDate = normalizeDateString(value);
    return parseToDateValue(normalizedDate);
  }
  return parseToDateValue(value);
};

export const preprocessNumber = (val: any) => {
  if (!val || val === "") return null;
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const parsed = parseFloat(val);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

export const preprocessString = (val: any) => {
  if (!val) return null;
  if (typeof val === "string") return val;
  if (typeof val === "object" && val.toString) return val.toString();
  return null;
};

// Generate Zod schema for open_fund_fields dynamically
export const openFundFieldsSchema = openFundFields.reduce(
  (acc, field) => {
    switch (field.type) {
      case "boolean":
        acc[field.code] = z.preprocess(
          (val) => parseYes(val as string),
          z.boolean().optional(),
        );
        break;
      case "number":
        acc[field.code] = z.preprocess(
          preprocessNumber,
          z.number().optional().nullable(),
        );
        break;
      case "datepicker":
        acc[field.code] = z
          .preprocess((val) => parseToDateValueEnhanced(val), dateSchema)
          .optional();
        break;
      case "countries":
        acc[field.code] = z.preprocess(
          preprocessString,
          z.string().nullable().optional(),
        );
        break;
      case "country":
      case "string":
      default:
        acc[field.code] = z.preprocess(
          preprocessString,
          z.string().nullable().optional(),
        );
        break;
    }
    return acc;
  },
  {} as Record<string, any>,
);

// Common FormSchema used by both new and edit forms
export const createFormSchema = () =>
  z.object({
    // Strategy autocomplete fields
    strategy_name: z.preprocess(
      (e: any) => preprocessTextAsyncAutocompleteField(e),
      z
        .union([
          z.string(),
          z.object({
            id: z.number(),
            name: z.string(),
            text: z.string(),
          }),
        ])
        .optional(),
    ),
    strategy_id: z.coerce.number().min(1, "Strategy is required"),

    // Other fields
    benchmark_name: z.string().nullable().optional(),
    replication_methodology: z.coerce.number().optional().nullable(),
    sfdr: z.string().nullable().optional(),
    is_funded: z.preprocess(
      (val) => parseYes(val as string),
      z.boolean().optional(),
    ),
    is_leveraged: z.preprocess(
      (val) => parseYes(val as string),
      z.boolean().optional(),
    ),
    is_ucits: z.preprocess(
      (val) => parseYes(val as string),
      z.boolean().optional(),
    ),
    distribution_policy: z.coerce.number().optional().nullable(),
    // Add all open_fund_fields
    ...openFundFieldsSchema,
  });
